import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiArrowLeft, FiVideo, FiPlay } from 'react-icons/fi';
import { liveAPI } from '../services/api';

const Live = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [liveVideos, setLiveVideos] = useState([]);

  useEffect(() => {
    // Redirect to home if not logged in
    if (!user) {
      navigate('/', { replace: true });
      return;
    }
    
    if (user && user.email) {
      fetchLiveVideos();
    } else {
      setLoading(false);
    }
  }, [user, navigate]);

  const fetchLiveVideos = async () => {
    try {
      setLoading(true);
      const response = await liveAPI.getLiveVideos(user.email);
      
      if (response.status === 200 && response.data.data) {
        setLiveVideos(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching live videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const playLiveVideo = (item) => {
    // Navigate to content viewer with video details
    navigate('/content-viewer', {
      state: {
        content: item.video_id,
        type: 'video',
        title: item.title,
        description: item.live_date || '',
      }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24 md:pb-0">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/dashboard" className="mr-4 p-2 rounded-lg hover:bg-gray-100 md:hidden">
                <FiArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <FiVideo className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-gray-800">Live Videos</h1>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {!user ? (
          /* Not Logged In */
          <div className="card text-center py-12 max-w-md mx-auto">
            <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiVideo className="w-12 h-12 text-red-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">Login Required</h3>
            <p className="text-gray-600 mb-6">Please login to access live videos</p>
            <Link to="/login" className="btn-primary inline-block px-8 py-3">
              Login Now
            </Link>
          </div>
        ) : loading ? (
          /* Loading State */
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary-500 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading live videos...</p>
          </div>
        ) : liveVideos.length === 0 ? (
          /* No Videos */
          <div className="card text-center py-12 max-w-md mx-auto">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <FiVideo className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">No Live Videos</h3>
            <p className="text-gray-600 mb-6">There are no live videos available at the moment</p>
            <Link to="/courses" className="btn-primary inline-block px-8 py-3">
              Browse Courses
            </Link>
          </div>
        ) : (
          /* Videos List */
          <div className="space-y-4">
            {liveVideos.map((item) => (
              <div
                key={item.id}
                className="card hover:shadow-xl transition-all"
              >
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Video Thumbnail */}
                  <div 
                    className="relative w-full md:w-48 h-32 rounded-lg overflow-hidden flex-shrink-0 cursor-pointer"
                    onClick={() => playLiveVideo(item)}
                  >
                    <img
                      src={`https://img.youtube.com/vi/${item.video_id}/mqdefault.jpg`}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23ef4444" width="400" height="300"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" fill="white" font-size="24"%3ELive%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center hover:bg-black/40 transition-all">
                      <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                        <FiPlay className="w-6 h-6 text-red-600 ml-1" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">
                        {item.title}
                      </h3>
                      {item.live_date && (
                        <p className="text-sm text-gray-600 mb-3 font-medium">
                          {item.live_date}
                        </p>
                      )}
                    </div>

                    {/* Live Now Button */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => playLiveVideo(item)}
                        className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg"
                      >
                        <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                        Live Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Live;
